import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { ToastProvider } from "../components/ToastSystem";
import { CookieConsentBannerWrapper } from "../wrappers";
import {
  AddToHomeScreen,
  markReturningUser,
} from "../components/AddToHomeScreen";
import { isCRESubdomain } from "../../utils/subdomainDetector";
import { ChunkErrorBoundary } from "../../components/ChunkErrorBoundary";
import { useReferralTracking } from "../../hooks/useReferralTracking";

// UTM capture is now handled by src/lib/attribution.ts (SOC-09),
// called during PostHog init. SessionStorage keys (fp_utm_*) are still
// written for backward compat with Stripe checkout.

export function RootLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  useReferralTracking();

  // UTM capture moved to src/lib/attribution.ts (SOC-09) — runs during
  // PostHog init, before React mounts, so the first event carries attribution.

  // Subdomain redirect: cre.fairpath.app → /commercial
  // If user is on the CRE subdomain but not on a /commercial route,
  // redirect them to the commercial landing page.
  useEffect(() => {
    if (isCRESubdomain() && !location.pathname.startsWith("/commercial")) {
      navigate(`/commercial${location.pathname === "/" ? "" : location.pathname}`, { replace: true });
    }
  }, [location.pathname, navigate]);

  // Scroll to top on route changes (SPA fix — React Router doesn't do this)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Global canonical URL fallback — ensures every route has a canonical tag
  // even if the page component forgets to pass `path` to usePageMeta.
  // Runs after a microtask so page-level usePageMeta (which runs in useEffect)
  // gets first shot. If canonical is already correct, this is a no-op.
  useEffect(() => {
    const BASE_URL = "https://fairpath.app";
    const pathname = location.pathname.replace(/\/+$/, "") || "/";
    const expected = pathname === "/" ? BASE_URL : `${BASE_URL}${pathname}`;

    // Defer so page-level usePageMeta runs first
    const id = requestAnimationFrame(() => {
      // Canonical link
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement("link");
        link.rel = "canonical";
        document.head.appendChild(link);
      }
      if (link.href !== expected) {
        link.href = expected;
      }

      // og:url
      let og = document.querySelector('meta[property="og:url"]') as HTMLMetaElement | null;
      if (!og) {
        og = document.createElement("meta");
        og.setAttribute("property", "og:url");
        document.head.appendChild(og);
      }
      if (og.content !== expected) {
        og.content = expected;
      }
    });
    return () => cancelAnimationFrame(id);
  }, [location.pathname]);

  // Clear chunk-reload guard on every successful navigation so each new
  // page visit gets one fresh reload attempt if a chunk fails to load.
  useEffect(() => {
    sessionStorage.removeItem("fp_chunk_reload");
  }, [location.pathname]);

  // Mark user as returning after 30 seconds on site
  useEffect(() => {
    const timer = setTimeout(() => {
      markReturningUser();
    }, 30_000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <ToastProvider>
      <div className="min-h-screen bg-white flex flex-col">
        <ChunkErrorBoundary>
          <Outlet />
        </ChunkErrorBoundary>
        <CookieConsentBannerWrapper />
        <AddToHomeScreen />
      </div>
    </ToastProvider>
  );
}
